import ExtensionIcon from '@/assets/icons/ExtensionIcon';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Collapse
} from '@mui/material';
import { useState } from 'react';
import {
  MdAccessTime,
  MdExpandLess,
  MdExpandMore,
  MdMailOutline,
  MdMoreVert,
  MdOutlineEdit,
  MdOutlineLocalPhone
} from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';

export default function OperatorCard({ operator, onEdit, onDelete, idx }: any) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(idx === 0 ? true : false);
  const theme = useTheme();

  const handleCloseMenu = () => setAnchorEl(null);

  const renderBreaks = (breaks?: { from: string; to: string }[]) => {
    if (!breaks || breaks.length === 0) {
      return (
        <Typography color='text.disabled' textAlign='center'>
          —
        </Typography>
      );
    }

    return (
      <Box display='flex' flexDirection='column' alignItems='center'>
        {breaks.map((b, index) => (
          <Typography key={index} fontSize={14} color={theme.palette.primary.main}>
            {b.from} - {b.to}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderBusinessHour = (day: string) => {
    const data = operator.working_hours?.[day.toLowerCase() as keyof typeof operator.business_hours];
    if (!data) return '—';
    return data.open && data.from_time && data.to_time ? `${data.from_time} - ${data.to_time}` : 'Closed';
  };

  return (
    <Box border='1px solid #ECECEC' borderRadius='12px' p={3} bgcolor='#fff'>
      {/* Header */}
      <Box display='flex' justifyContent='space-between'>
        <Box>
          <Typography fontWeight={500}>{operator?.first_name}</Typography>

          <Stack direction='row' spacing={10} mt={0.5}>
            {operator.email && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <MdMailOutline color={theme.palette.primary.main} />
                <Typography variant='body2'>{operator.email}</Typography>
              </Stack>
            )}
            {operator.phone_number && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <MdOutlineLocalPhone color={theme.palette.primary.main} />
                <Typography variant='body2'>{operator.phone_number}</Typography>
              </Stack>
            )}
            {operator.sip_uri && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <ExtensionIcon color={theme.palette.primary.main} />
                <Typography variant='body2'>{operator.sip_uri}</Typography>
              </Stack>
            )}
            {operator.extension && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <ExtensionIcon color={theme.palette.primary.main} />
                <Typography variant='body2'>{operator.extension}</Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MdMoreVert />
        </IconButton>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onEdit();
            }}
          >
            <MdOutlineEdit /> Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onDelete();
            }}
            sx={{ color: 'red' }}
          >
            <RiDeleteBin6Line /> Delete
          </MenuItem>
        </Menu>
      </Box>

      <Box mt={2} border='1px solid #E5E7EB' borderRadius={2} p={1.5} bgcolor='#FFFFFF' sx={{ overflow: 'hidden' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpen(!open)}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <MdAccessTime color={theme.palette.primary.main} size={18} />
            <Typography variant='body2' fontWeight={500}>
              Office Hours
            </Typography>
          </Stack>
          {open ? <MdExpandLess /> : <MdExpandMore />}
        </Stack>

        <Collapse in={open}>
          <Box
            mt={1.5}
            border='1px solid #E5E7EB'
            borderRadius={2}
            overflow='auto'
            sx={{
              backgroundColor: '#FFFFFF',
              width: '100%',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: 4
              }
            }}
          >
            <Table
              size='small'
              sx={{
                minWidth: 700,
                borderCollapse: 'separate',
                borderSpacing: 0
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid #E5E7EB' }}> {} </TableCell>
                  {days.map((day: any) => (
                    <TableCell
                      key={day}
                      align='center'
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        borderRight: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {day}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      borderRight: '1px solid #E5E7EB',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    SCHEDULE
                  </TableCell>
                  {days.map((day: any) => (
                    <TableCell
                      key={day}
                      align='center'
                      sx={{
                        color: theme.palette.primary.main,
                        borderRight: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderBusinessHour(day)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      borderRight: '1px solid #E5E7EB'
                    }}
                  >
                    BREAKS
                  </TableCell>
                  {days.map((day: any) => (
                    <TableCell
                      key={day}
                      align='center'
                      sx={{
                        color: theme.palette.primary.main,
                        borderRight: '1px solid #E5E7EB',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderBreaks(
                        (operator?.working_hours?.[day.toLowerCase() as keyof typeof operator.working_hours] as any)
                          ?.breaks
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
